import { LocalizedDate } from 'components/Basic/Date/LocalizedDate';
import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import { Flag } from 'components/Basic/Flag/Flag';
import Heading from 'components/Basic/Heading/Heading';
import Image from 'components/Basic/Image/Image';
import LimitHeightWrap from 'components/Helpers/LimitHeightWrap/LimitHeightWrap';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { memo } from 'react';
import { twJoin } from 'tailwind-merge';
import { ListedBlogArticleType } from 'types/blogArticle';
import { SimpleBlogCategoryType } from 'types/blogCategory';
import twMerge from 'utils/twMerge';

type BlogArticlesListProps = {
    blogArticles: ListedBlogArticleType[];
    promotedArticle: ListedBlogArticleType | null;
    showPromotedArticle: boolean;
};

const TEST_IDENTIFIER = 'pages-blogcategory-blogarticleslist-';

const articleListTwClass = {
    listItem: '[&:nth-child(n_+_2)]:max-w-[400px] mt-8',
    listItemImageWrapper: 'mb-4 max-w-[700px] overflow-hidden',
    listItemImageLink: 'ease-defaultTransition duration-200 hover:opacity-80 block',
    listItemDate: 'text-tiny whitespace-nowrap',
    listItemTitleWrapper:
        'mb-2 block text-primary no-underline duration-200 ease-defaultTransition hover:text-green hover:no-underline',
    flagList: 'flex flex-wrap gap-2',
    infoList: 'flex flex-wrap items-center gap-x-5 gap-y-2 [&:not(:last-child)]:mb-2',
};

export const BlogArticlesList: FC<BlogArticlesListProps> = memo(
    ({ blogArticles, promotedArticle, showPromotedArticle }) => {
        const t = useTypedTranslationFunction();

        const reduceCategories = (blogCategories: SimpleBlogCategoryType[]): SimpleBlogCategoryType[] => {
            const parentNames = [];

            for (const blogCategory of blogCategories) {
                if (blogCategory.parent !== null) {
                    parentNames.push(blogCategory.parent.name);
                }
            }

            const reducedCategories = [];

            for (const blogCategory of blogCategories) {
                if (!parentNames.includes(blogCategory.name)) {
                    reducedCategories.push(blogCategory);
                }
            }

            return reducedCategories;
        };

        return (
            <ul className="mb-10 grid grid-cols-1 gap-x-3 md:grid-cols-2 md:gap-x-2">
                {showPromotedArticle && promotedArticle !== null && (
                    <li
                        className={twJoin(articleListTwClass.listItem, 'md:col-span-2')}
                        key={promotedArticle.uuid}
                        data-testid={TEST_IDENTIFIER + '-promoted'}
                    >
                        {promotedArticle.image !== null && (
                            <div
                                className={twMerge(
                                    articleListTwClass.listItemImageWrapper,
                                    'h-72 notVl:-mx-5 notVl:h-auto',
                                )}
                                data-testid={TEST_IDENTIFIER + '-promoted-image'}
                            >
                                <ExtendedNextLink
                                    type="blogArticle"
                                    href={promotedArticle.link}
                                    className={articleListTwClass.listItemImageLink}
                                >
                                    <Image
                                        src={promotedArticle.image}
                                        width="700"
                                        priority
                                        height="288"
                                        alt={promotedArticle.name}
                                    />
                                </ExtendedNextLink>
                            </div>
                        )}

                        <ExtendedNextLink
                            type="blogArticle"
                            href={promotedArticle.link}
                            className={articleListTwClass.listItemTitleWrapper}
                            data-testid={TEST_IDENTIFIER + '-promoted-title'}
                        >
                            <Heading className="text-[30px] leading-8 text-currentColor" type="h2">
                                {promotedArticle.name}
                            </Heading>
                        </ExtendedNextLink>
                        <div className={articleListTwClass.infoList}>
                            <div className={articleListTwClass.flagList}>
                                {reduceCategories(promotedArticle.blogCategories).map(
                                    (blogArticleCategory, blogArticleCategoryIndex) => {
                                        return (
                                            blogArticleCategory.parent !== null && (
                                                <Flag
                                                    backgroundColor="#ffffff"
                                                    data-testid={
                                                        TEST_IDENTIFIER +
                                                        '-promoted-section-' +
                                                        blogArticleCategoryIndex
                                                    }
                                                    key={blogArticleCategory.uuid}
                                                    flagLink={blogArticleCategory.link}
                                                >
                                                    {blogArticleCategory.name}
                                                </Flag>
                                            )
                                        );
                                    },
                                )}
                            </div>
                            <span
                                className={articleListTwClass.listItemDate}
                                data-testid={TEST_IDENTIFIER + '-promoted-date'}
                            >
                                <LocalizedDate date={promotedArticle.publishDate} />
                            </span>
                        </div>

                        {promotedArticle.perex !== null && (
                            <div
                                className="mb-0 text-body font-normal leading-6 lg:text-h6 lg:font-light lg:leading-8"
                                data-testid={TEST_IDENTIFIER + '-promoted-perex'}
                            >
                                <LimitHeightWrap
                                    mobileHeight={240}
                                    desktopHeight={130}
                                    buttonLabelMore={t('More')}
                                    buttonAlign="left"
                                >
                                    {promotedArticle.perex}
                                </LimitHeightWrap>
                            </div>
                        )}
                    </li>
                )}

                {blogArticles.map(({ uuid, link, name, image, blogCategories, publishDate }, index) => (
                    <li
                        className={twJoin(articleListTwClass.listItem, 'basis-[calc(50%-40px)]')}
                        key={uuid}
                        data-testid={TEST_IDENTIFIER + index}
                    >
                        {image !== null && (
                            <div
                                className={twJoin(articleListTwClass.listItemImageWrapper, 'h-40 notVl:h-auto')}
                                data-testid={TEST_IDENTIFIER + index + '-image'}
                            >
                                <ExtendedNextLink
                                    type="blogArticle"
                                    href={link}
                                    className={articleListTwClass.listItemImageLink}
                                >
                                    <Image src={image} width="390" height="161" alt={name} />
                                </ExtendedNextLink>
                            </div>
                        )}
                        <ExtendedNextLink
                            type="blogArticle"
                            href={link}
                            className={articleListTwClass.listItemTitleWrapper}
                            data-testid={TEST_IDENTIFIER + index + '-title'}
                        >
                            <Heading className="text-h5 leading-8 text-currentColor" type="h2">
                                {name}
                            </Heading>
                        </ExtendedNextLink>
                        <div className={articleListTwClass.infoList}>
                            <div className={articleListTwClass.flagList}>
                                {reduceCategories(blogCategories).map(
                                    (blogArticleCategory, blogArticleCategoryIndex) => {
                                        return (
                                            blogArticleCategory.parent !== null && (
                                                <Flag
                                                    backgroundColor="#ffffff"
                                                    data-testid={
                                                        TEST_IDENTIFIER + index + '-section-' + blogArticleCategoryIndex
                                                    }
                                                    flagLink={blogArticleCategory.link}
                                                    key={blogArticleCategory.uuid}
                                                >
                                                    {blogArticleCategory.name}
                                                </Flag>
                                            )
                                        );
                                    },
                                )}
                            </div>
                            <span
                                className={articleListTwClass.listItemDate}
                                data-testid={TEST_IDENTIFIER + index + '-date'}
                            >
                                <LocalizedDate date={publishDate} />
                            </span>
                        </div>
                    </li>
                ))}
            </ul>
        );
    },
);

BlogArticlesList.displayName = 'BlogArticlesList';
